import { Injectable, inject } from '@angular/core';
import {
  Auth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  sendEmailVerification,
  User,
  onAuthStateChanged,
} from '@angular/fire/auth';
import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { Observable, BehaviorSubject } from 'rxjs';
import { Firestore, doc, getDoc, setDoc, updateDoc, serverTimestamp } from '@angular/fire/firestore';

const USERS_COLLECTION = 'users';

/**
 * AuthService: Firebase Auth (email/contraseña y Google OAuth).
 * Colección `users`: doc id = Auth UID; campos role (root|user), personaId (ref. personas). Ver docs/ESTRUCTURA_DATOS_COOPERATIVAS_PERSONAS.md.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private auth = inject(Auth);
  private firestore = inject(Firestore);
  private authStateSubject = new BehaviorSubject<User | null>(null);
  private authReadyPromise: Promise<void>;

  constructor() {
    this.authReadyPromise = new Promise((resolve) => {
      onAuthStateChanged(this.auth, (user) => {
        this.authStateSubject.next(user);
        resolve();
      });
    });
  }

  getCurrentUser(): User | null {
    return this.auth.currentUser;
  }

  authState$(): Observable<User | null> {
    return this.authStateSubject.asObservable();
  }

  async authReady(): Promise<void> {
    await this.authReadyPromise;
    await this.auth.authStateReady();
    const user = this.auth.currentUser;
    if (user) await user.getIdToken(true);
  }

  async login(email: string, password: string): Promise<void> {
    const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
    await userCredential.user.getIdToken(true);
  }

  async loginWithGoogle(): Promise<void> {
    const provider = new GoogleAuthProvider();
    const userCredential = await signInWithPopup(this.auth, provider);
    await userCredential.user.getIdToken(true);
  }

  async register(email: string, password: string): Promise<void> {
    const userCredential = await createUserWithEmailAndPassword(this.auth, email, password);
    if (userCredential.user) {
      const continueUrl = typeof window !== 'undefined' && window.location?.origin
        ? `${window.location.origin}/login`
        : undefined;
      await sendEmailVerification(userCredential.user, continueUrl ? { url: continueUrl, handleCodeInApp: false } : undefined);
    }
  }

  async logout(): Promise<void> {
    await signOut(this.auth);
  }

  async sendPasswordResetEmail(email: string): Promise<void> {
    await firebaseSendPasswordResetEmail(this.auth, email);
  }

  isAuthenticated(): boolean {
    return this.auth.currentUser !== null;
  }

  /**
   * Rol del usuario a nivel sistema (users/{uid}.role).
   * 'root' = administrador de plataforma; 'user' u otro = usuario normal.
   * El rol por cooperativa está en cooperativa/{cooperativaId}/usuarios/{uid}.role. Ver docs/ROLES_USUARIOS.md.
   */
  async getCurrentUserRole(): Promise<string | undefined> {
    const user = this.auth.currentUser;
    if (!user) return undefined;
    const ref = doc(this.firestore, USERS_COLLECTION, user.uid);
    const snap = await getDoc(ref);
    const role = snap.data()?.['role'];
    return typeof role === 'string' ? role : undefined;
  }

  /** True si el usuario tiene rol root (acceso admin plataforma). */
  async isRoot(): Promise<boolean> {
    return (await this.getCurrentUserRole()) === 'root';
  }

  /**
   * Asegura que exista el documento users/{uid} con el mismo ID que Firebase Auth.
   * Se llama tras el login para mantener la relación auth ↔ users (como en StockDigiCom).
   * Si el documento ya existe, no se sobrescribe. Si no existe, se crea con email, displayName, photoURL, etc.
   */
  async ensureUserDocument(): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) return;

    const uid = user.uid;
    const ref = doc(this.firestore, USERS_COLLECTION, uid);
    const snap = await getDoc(ref);
    if (snap.exists()) return;

    const payload: Record<string, unknown> = {
      email: user.email ?? '',
      emailVerified: user.emailVerified === true,
      role: 'user', // Rol a nivel sistema. root se asigna a mano en Firestore. Ver docs/ROLES_USUARIOS.md.
    };
    if (user.displayName) payload['displayName'] = user.displayName;
    if (user.photoURL) payload['photoURL'] = user.photoURL;
    if (user.phoneNumber) payload['phone'] = user.phoneNumber;

    await setDoc(ref, payload);
  }

  /** Cooperativa preferida para entrar al workspace (`users/{uid}.cooperativaPreferidaId`). */
  async getCooperativaPreferidaId(): Promise<string | null> {
    const user = this.auth.currentUser;
    if (!user) return null;
    const snap = await getDoc(doc(this.firestore, USERS_COLLECTION, user.uid));
    if (!snap.exists()) return null;
    const v = snap.data()?.['cooperativaPreferidaId'];
    return typeof v === 'string' && v.trim() ? v.trim() : null;
  }

  /**
   * Persiste la cooperativa preferida (p. ej. pantalla de configuración del usuario).
   * No usar al cambiar de cooperativa en el modal del workspace (eso es solo sesión / `localStorage`).
   */
  async setCooperativaPreferidaId(cooperativaId: string | null): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) return;
    const ref = doc(this.firestore, USERS_COLLECTION, user.uid);
    await updateDoc(ref, {
      cooperativaPreferidaId: cooperativaId,
      updatedAt: serverTimestamp(),
    });
  }

  handleAuthError(error: unknown): Error {
    const e = error as { code?: string; message?: string };
    let message = e?.message ?? 'Error desconocido';
    switch (e?.code) {
      case 'auth/email-already-in-use':
        message = 'Este correo ya está registrado';
        break;
      case 'auth/invalid-email':
        message = 'El correo no es válido';
        break;
      case 'auth/weak-password':
        message = 'La contraseña es muy débil';
        break;
      case 'auth/user-not-found':
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        message = 'Credenciales inválidas';
        break;
      case 'auth/popup-blocked':
        message = 'Permití ventanas emergentes para este sitio';
        break;
      case 'auth/popup-closed-by-user':
        message = 'Cerraste la ventana de inicio de sesión';
        break;
      case 'auth/operation-not-allowed':
        message = 'Este método de inicio de sesión no está habilitado en Firebase';
        break;
      case 'auth/too-many-requests':
        message = 'Demasiados intentos. Intentá más tarde';
        break;
    }
    return new Error(message);
  }
}
